import React, { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Form,
  Row,
} from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BreadCrumbs from "../../../../../components/BreadCrumbs";
import TableLoadingComponent from "../../../../../components/TableLoadingComponent";
import ViewButtonComponent from "../../../../../components/ViewButtonComponent";
import { useFirebaseCartSummaries } from "../../../../../hooks/useFirebaseCartSummaries";
import "./index.css";

export interface ProductInventory {
  id: string;
  productId: string;
  displayName: string;
  totalAvailable: number;
  totalReserved: number;
  totalSold: number;
  totalAllocated: number;
  totalReceived: number;
  channels: {
    [channelName: string]: {
      available: number;
      reserved: number;
      sold: number;
      allocated: number;
    };
  };
}

const InventoryView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { restaurantId, cartId } = location.state || {};

  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );

  // Get restaurant ID from Redux if not passed in state
  const reduxRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );

  const effectiveRestaurantId = restaurantId || reduxRestaurantId;

  // Use the cart summaries hook
  const {
    data: cartSummaries,
    loading,
    error,
  } = useFirebaseCartSummaries(effectiveRestaurantId, new Date(selectedDate));

  // Get current cart data
  const currentCart = useMemo(() => {
    if (!cartSummaries || !cartId) return null;
    return cartSummaries.find((cart) => cart.id === cartId);
  }, [cartSummaries, cartId]);

  // Process products ONLY from the selected cart
  const products = useMemo<ProductInventory[]>(() => {
    if (!currentCart || !currentCart.products) return [];

    const productMap = new Map<string, ProductInventory>();

    Object.values(currentCart.products).forEach((product) => {
      const productKey = product.id;

      if (!productMap.has(productKey)) {
        productMap.set(productKey, {
          id: product.id,
          productId: product.productId,
          displayName: product.displayName,
          totalAvailable: 0,
          totalReserved: 0,
          totalSold: 0,
          totalAllocated: 0,
          totalReceived: 0,
          channels: {},
        });
      }

      const existingProduct = productMap.get(productKey)!;

      Object.entries(product.channels || {}).forEach(([channelName, channel]) => {
        // Update product totals
        existingProduct.totalAvailable += channel.available || 0;
        existingProduct.totalReserved += channel.reserved || 0;
        existingProduct.totalSold += channel.sold || 0;
        existingProduct.totalAllocated += channel.allocated || 0;

        // Calculate total received (available + sold + reserved + allocated)
        existingProduct.totalReceived +=
          (channel.available || 0) +
          (channel.sold || 0) +
          (channel.reserved || 0) +
          (channel.allocated || 0);

        // Update channel data
        if (!existingProduct.channels[channelName]) {
          existingProduct.channels[channelName] = {
            available: 0,
            reserved: 0,
            sold: 0,
            allocated: 0,
          };
        }

        existingProduct.channels[channelName].available +=
          channel.available || 0;
        existingProduct.channels[channelName].reserved +=
          channel.reserved || 0;
        existingProduct.channels[channelName].sold += channel.sold || 0;
        existingProduct.channels[channelName].allocated +=
          channel.allocated || 0;
      });
    });

    return Array.from(productMap.values());
  }, [currentCart]);

  const handleView = (product: ProductInventory) => {
    navigate("/carts/carts-list/view/inventory-view/item-view", {
      state: {
        product,
        selectedDate,
        cartId,
        cartName: currentCart?.cartName || "",
        restaurantId: effectiveRestaurantId,
      },
    });
  };

  const columns: TableColumn<ProductInventory>[] = [
    {
      name: "Product Name",
      selector: (row) => row.displayName,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <div>
          <div className="fw-medium">{row.displayName}</div>
          <div className="text-muted small">ID: {row.productId}</div>
        </div>
      ),
    },
    {
      name: "Available",
      selector: (row) => row.totalAvailable,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span
          className={`fw-bold ${row.totalAvailable <= 0 ? "text-danger" : "text-success"}`}
        >
          {row.totalAvailable}
        </span>
      ),
    },
    {
      name: "Reserved",
      selector: (row) => row.totalReserved,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="text-warning fw-medium">{row.totalReserved}</span>
      ),
    },
    {
      name: "Allocated",
      selector: (row) => row.totalAllocated,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="text-info fw-medium">{row.totalAllocated}</span>
      ),
    },
    {
      name: "Sold",
      selector: (row) => row.totalSold,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <span className="text-primary fw-medium">{row.totalSold}</span>
      ),
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-2">
          <ViewButtonComponent onClick={() => handleView(row)} />
        </div>
      ),
      width: "100px",
      center: true,
    },
  ];

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  if (error) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="alert alert-danger">
              <h5>Error Loading Inventory</h5>
              <p>{error.message}</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  // If no cartId is provided, show error
  if (!cartId) {
    return (
      <>
        <BreadCrumbs hasSubMenus={false} />
        <Card className="mt-3">
          <CardBody>
            <div className="alert alert-warning">
              <h5>Cart Not Selected</h5>
              <p>Please select a cart to view inventory.</p>
            </div>
          </CardBody>
        </Card>
      </>
    );
  }

  return (
    <>
      <BreadCrumbs hasSubMenus={false} />

      {/* Date Filter */}
      <Card className="mt-3">
        <CardBody>
          <Row className="align-items-center">
            <Col md={6}>
              <h4 className="inventory-headers mb-0">Cart Inventory</h4>
              {currentCart && (
                <div className="mt-2">
                  <p className="mb-1">
                    <strong>Cart:</strong> {currentCart.cartName}
                  </p>
                
                </div>
              )}
            </Col>
            <Col md={4} className="ms-auto">
              <Form.Group>
                <Form.Label>Select Date:</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </Form.Group>
            </Col>
          </Row>
        </CardBody>
          <CardBody>
          {loading ? (
            <TableLoadingComponent numColumns={8} />
          ) : products.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">
                No products found in this cart for {selectedDate}
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={products}
              responsive
              pagination
              highlightOnHover
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              noHeader
              className="border-0"
              striped
            />
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default InventoryView;